import {
  AfterViewInit,
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  Renderer2,
  ChangeDetectorRef,
} from '@angular/core';
import { Router } from '@angular/router';
import { IonContent } from '@ionic/angular/lazy';
import { IOrganizationPages } from 'src/app/auth/models/organization-pages';
import { DashboardService } from 'src/app/dashboard/services/dashboard.service';
import { UserService } from 'src/app/user/services/user.service';
import { NotificationService } from '../services/notification/notification.service';
import { CurrentUrlService } from 'src/app/user/services/current-url.service';

@Component({
  selector: 'app-org-terms-conditions',
  templateUrl: './org-terms-conditions.component.html',
  styleUrls: ['./org-terms-conditions.component.scss'],
  standalone: false
})
export class OrgTermsConditionsComponent implements OnInit, AfterViewInit {
  @ViewChild(IonContent, { static: false }) content!: IonContent;
  @ViewChild('termsBox', { static: false }) termsBox!: ElementRef;

  isAccepted: boolean = false;
  hasScrolledToBottom: boolean = false;

  termsContent: string = '';
  termsTitle: string = '';

  tolerance = 150; // pixels from bottom (adjust if needed)

  constructor(
    private dashboardService: DashboardService,
    private router: Router,
    private userService: UserService,
    private notificationService: NotificationService,
    private currentUrlService: CurrentUrlService,
    private renderer: Renderer2,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.currentUrlService.changeUrl('terms');
    this.getOrganizationPages();
  }

  ngAfterViewInit(): void {
    this.resetScrollState();
  }

  // Main scroll handler
  onContentScroll(event: any): void {
    this.checkIfScrolledToBottom();
  }

  // This is the bullet-proof checker
  private checkIfScrolledToBottom(): void {
    if (!this.content) return;

    this.content.getScrollElement().then((scrollElement: HTMLElement) => {
      const scrollTop = scrollElement.scrollTop;
      const scrollHeight = scrollElement.scrollHeight;
      const clientHeight = scrollElement.clientHeight;

      const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);

      if (distanceFromBottom <= this.tolerance) {
        this.hasScrolledToBottom = true;
        this.cdr.detectChanges(); // force update if needed
      }
    });
  }

  // Run this every time terms content is updated
  private onTermsContentChanged(): void {
    // Wait for Angular to render the [innerHTML]
    setTimeout(() => {
      this.resetScrollState();
      this.checkIfScrolledToBottom();

      // Also listen to any DOM mutations inside termsBox (in case of images, lazy content, etc.)
      if (this.termsBox?.nativeElement) {
        const observer = new MutationObserver(() => {
          setTimeout(() => this.checkIfScrolledToBottom(), 100);
        });
        observer.observe(this.termsBox.nativeElement, {
          childList: true,
          subtree: true,
          attributes: true,
        });
      }
    }, 150);
  }

  private resetScrollState(): void {
    this.hasScrolledToBottom = false;
    this.isAccepted = false;
    this.content?.scrollToTop(0);
  }

  scrollToTop(): void {
    this.content?.scrollToTop(400);
  }

  submitAcceptance(consent: number): void {
    if (consent === 1 && !this.hasScrolledToBottom) {
      this.notificationService.presentToast(
        'Please scroll to the bottom...',
        '',
        'warning'
      );
      return;
    }

    const obj = { terms_consent: consent };

    this.userService.updateTermConsent(obj).subscribe({
      next: (response: any) => {
        if (response?.isValid) {
          this.notificationService.presentToast(
            response.message,
            '',
            'success'
          );

          this.router.navigate(['/tabs/dashboard/home']);
        } else {
          this.notificationService.presentToast(response.message, '', 'danger');
        }
      },
      error: (err) => {
        this.notificationService.presentToast(
          'Error updating consent',
          '',
          'danger'
        );
      },
    });
  }

  getOrganizationPages(): void {
    this.dashboardService.getOrganizationPages().subscribe({
      next: (res: IOrganizationPages) => {
        const pages = res.data || [];
        const termsPage = pages.find(
          (p: any) => p?.page_name.toLowerCase() === 'terms & conditions'
        );

        if (termsPage?.content) {
          try {
            const parsed = JSON.parse(termsPage.content);
            this.termsTitle = parsed.title || 'Terms & Conditions';
            this.termsContent =
              parsed.content || '<p>No content available.</p>';
          } catch (e) {
            console.error('JSON parse error:', e);
            this.termsContent = '<p>Error loading terms.</p>';
          }
        } else {
          this.termsContent = '<p>Terms & Conditions not found.</p>';
        }

        // VERY IMPORTANT: re-check scroll after content is rendered
        this.onTermsContentChanged();
      },
      error: (err) => {
        console.error(err);
        this.termsContent = '<p>Failed to load Terms & Conditions.</p>';
        this.onTermsContentChanged();
      },
    });
  }
}
